import { $analytics } from '@/boot/analytics'
import { $pegApi } from '@/boot/peg-api'
import { $rollbar } from '@/boot/rollbar'
import { LocalStorage, Notify } from 'quasar'
import { enrichAuctionItem } from '@/utils/product.util'
import { PICKUP, DELIVERY } from '@/constants/fulfillment.const'
import { useQuery, useQueryClient } from '@tanstack/vue-query'

export const useCartStore = defineStore('cart', () => {
  const queryClient = useQueryClient()

  const cartItems = ref([])
  const cartIsAdding = ref(false)
  const cartIsRemoving = ref(false)
  const cartHasAuctionWins = computed(() => {
    return cartItems.value.some(item => item.won)
  })
  const cartHasAutoCheckoutWins = computed(() => {
    return cartItems.value.some(item => item.won && useUserStore().userHasAutoCheckout)
  })
  const cartHasBuyNowItem = computed(() => {
    return cartItems.value.some(item => !item.won)
  })
  const cartSelectedItemIds = ref(LocalStorage.getItem('peg_checkout_items') || [])
  const cartSelectedItems = computed(() => {
    return cartItems.value.filter(item => cartSelectedItemIds.value.includes(item.auctionItemId))
  })
  const hasDeliveryFulfillment = computed(() => cartSelectedItems.value.some(item => item.isDeliveryFulfillment))
  const hasPickupFulfillment = computed(() => cartSelectedItems.value.some(item => item.isPickupFulfillment))

  const shoppingZip = computed(() => useSessionStore().shoppingZip)
  const sessionUserIsEstablished = computed(() => useSessionStore().sessionUserIsEstablished)
  const { isReturnRoute } = useRouteMap()
  /**
   * Fetch active cart items
   */
  const {
    data: cartData,
    isPending: cartIsPending,
    isFetching: cartIsFetching,
    isError: cartHasError,
    error: cartError,
    refetch: fetchCart,
  } = useQuery({
    queryKey: ['cart', shoppingZip],
    queryFn: () => $pegApi.Cart.get({ zip: shoppingZip.value }, { silent: true }),
    enabled: computed(() => sessionUserIsEstablished.value && !!shoppingZip.value),
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * Math.pow(2, attemptIndex), 4000),
  })

  watch(
    cartData,
    curr => {
      if (curr?.cart) {
        setCartItems(curr.cart.items)
      }
    },
    { immediate: true }
  )

  watch(cartHasError, curr => {
    if (curr) {
      const err = cartError.value
      $rollbar.error('Error fetching cart', {
        errorMessage: err?.message || String(err),
        statusCode: err?.statusCode || err?.status,
        endpoint: '/cart',
        zip: shoppingZip.value,
        userEstablished: sessionUserIsEstablished.value,
      })

      if (!isReturnRoute.value) {
        // Surface failure to the user — empty cart UI alone is misleading on fetch errors
        Notify.create({
          type: 'negative',
          message: 'We were unable to load your cart.',
          caption: 'Please try again in a moment.',
        })
      }
    }
  })

  // persist selected items in local storage and ensure they are still valid when cart items change
  watchEffect(() => {
    if (!cartIsPending.value && !cartIsFetching.value && cartItems.value.length) {
      if (!cartSelectedItemIds.value.length) {
        cartSelectedItemIds.value = cartItems.value.map(item => item.auctionItemId)
      }

      // ensure all selected items still exist in cart and update local storage
      cartSelectedItemIds.value = cartSelectedItemIds.value.filter(sid =>
        cartItems.value.some(item => item.auctionItemId === sid && (!item.won || !useUserStore().userHasAutoCheckout))
      )
      LocalStorage.setItem('peg_checkout_items', cartSelectedItemIds.value)
    }
  })

  const totalQty = computed(() => cartItems.value.length)

  // group cart items by status
  const cartItemGroups = computed(() => {
    // const groups = []
    const groupTypes = cartItems.value
      // sort by expiry time
      .sort((a, b) => new Date(a.purchaseExpireTime).getTime() - new Date(b.purchaseExpireTime).getTime())
      // group into upForGrabs and liveAuction
      .reduce(
        (acc, item) => {
          let key

          switch (item.status) {
            case 'EXPIRED':
              key = 'expired'
              break

            case 'WON':
            case 'UNPAID':
              key = 'won'
              break

            default:
              key = 'added'
              break
          }

          acc[key].push(item)

          return acc
        },
        { added: [], won: [], expired: [] }
      )

    // won items come first
    // if ('won' in groupTypes) {
    //   groups.push(groupTypes.won)
    // }
    // if ('added' in groupTypes) {
    //   groups.push(groupTypes.cart)
    // }
    // if ('expired' in groupTypes) {
    //   groups.push(groupTypes.expired)
    // }

    return groupTypes
  })

  //
  function getTotalLineItemPrice(items) {
    return items.reduce((acc, item) => acc + item.auctionPurchasePrice, 0)
  }

  function getTotalShipping(items) {
    return items.reduce((acc, item) => {
      acc += item.isDeliveryFulfillment ? item.fulfillmentOptions?.delivery?.cost || 0 : 0
      return acc
    }, 0)
  }

  function getTotalTax(items) {
    return items.reduce((acc, item) => {
      acc += item?.estimatedTax || 0
      return acc
    }, 0)
  }

  function getTotalPrice(items) {
    return getTotalLineItemPrice(items) + getTotalTax(items) + getTotalShipping(items)
  }

  function getSummary(items) {
    return {
      totalLineItemPrice: getTotalLineItemPrice(items),
      totalShipping: getTotalShipping(items),
      totalTax: getTotalTax(items),
      totalPrice: getTotalPrice(items),
    }
  }

  /**
   * Reset cart state (called on logout)
   */
  function resetState() {
    cartItems.value = []
    cartSelectedItemIds.value = []
    // Clear localStorage
    LocalStorage.removeItem('peg_checkout_items')
    // Invalidate cart query to refetch fresh data when needed (e.g. on login)
    queryClient.removeQueries({ queryKey: ['cart'] })
  }

  /**
   * Set cart items and enhance to expected format
   * @param {Array} items - Cart items
   * @returns {void}
   */
  function setCartItems(items) {
    cartItems.value = items.map(item => {
      item = {
        ...item,
        ...item.auctionItem, // include auctionItem data at top level
        id: item.id, // ensure cart item id persists given it would be overwritten by auctionItem.id
        isDeliveryFulfillment: item.fulfillmentMethod === DELIVERY,
        isPickupFulfillment: item.fulfillmentMethod === PICKUP,
      }

      return enrichAuctionItem(item)
    })
  }

  /**
   * Add the current item to the user's cart
   */
  async function addItem(item) {
    cartIsAdding.value = true

    try {
      const { retailer } = await useRetailerPublicStore().fetchPublicRetailer(item.retailerId)
      const { featureFlags } = useFeatureFlagStore()

      // Delivery requires both the feature flag and the retailer to support it
      const deliveryEnabled = featureFlags.fulfillmentDelivery?.enabled && retailer?.fulfillmentMethod === DELIVERY
      const fulfillmentMethod = deliveryEnabled ? DELIVERY : PICKUP

      const body = { auctionItemId: item.id, fulfillmentMethod }
      const { cart } = await $pegApi.Cart.addItem(body, { zip: useSessionStore().shoppingZip })

      // update cart items
      setCartItems(cart.items)

      $analytics.interaction('Added to Cart', {
        $value: cartItems.value.reduce((acc, i) => acc + i.auctionPurchasePrice, 0),
        AddedItemProductName: item.title,
        AddedItemProductID: item.id,
        AddedItemSKU: item.sku,
        // AddedItemCategories: ['Fiction', 'Classics', 'Children'],
        AddedItemImageURL: item.images?.[0]?.url || '',
        AddedItemURL: item.fullProductUrl,
        AddedItemPrice: item.auctionPurchasePrice,
        AddedItemQuantity: 1,
        ItemNames: cartItems.value.map(i => i.title),
        CheckoutURL: `${window.location.origin}/checkout`,
        Items: cartItems.value.map(item => ({
          ProductID: item.id,
          SKU: item.sku,
          ProductName: item.title,
          Quantity: item.quantity || 1,
          ItemPrice: item.auctionPurchasePrice,
          RowTotal: item.auctionPurchasePrice * (item.quantity || 1),
          ProductURL: item.fullProductUrl,
          ImageURL: item.images?.[0]?.url || '',
          // ProductCategories: item.categories || [],
        })),
      })

      Notify.create({
        type: 'success',
        message: 'Item added to cart',
      })

      return { success: true }
    } catch (error) {
      $rollbar.error('Error adding item to cart', error)

      Notify.create({
        type: 'error',
        message: 'Could not add item to cart, please try again.',
      })

      return { success: false }
    } finally {
      cartIsAdding.value = false
    }
  }

  /**
   * Remove an item from the cart
   */
  async function removeItem(itemId) {
    cartIsRemoving.value = true

    try {
      const { cart } = await $pegApi.Cart.removeItem(itemId, { zip: useSessionStore().shoppingZip })

      // $analytics.interaction('Removed from Cart', { item_id: itemId })

      // update cart items
      setCartItems(cart.items)

      Notify.create({
        type: 'success',
        message: 'Item removed from cart',
      })

      return { success: true }
    } catch (error) {
      $rollbar.error('Error removing item from cart', error)

      Notify.create({
        type: 'error',
        message: 'Could not remove item from cart, please try again.',
      })

      return { success: false }
    } finally {
      cartIsRemoving.value = false
    }
  }

  /**
   * Add an item to the checkout
   */
  function addCheckoutItem(item) {
    // if item is not already selected, add it
    if (!cartSelectedItemIds.value.some(sid => sid === item.auctionItemId)) {
      cartSelectedItemIds.value.push(item.auctionItemId)
    }
  }

  /**
   * Remove an item from the checkout
   */
  async function removeCheckoutItem(itemId) {
    cartSelectedItemIds.value = cartSelectedItemIds.value.filter(sid => sid !== itemId)
  }

  return {
    // STATE
    cartItems,
    cartIsPending,
    cartIsFetching,
    cartHasError,
    cartIsAdding,
    cartIsRemoving,
    cartSelectedItemIds,
    // GETTERS
    cartSelectedItems,
    cartItemGroups,
    totalQty,
    hasDeliveryFulfillment,
    hasPickupFulfillment,
    cartHasAuctionWins,
    cartHasAutoCheckoutWins,
    cartHasBuyNowItem,
    // ACTIONS
    addItem,
    fetchCart,
    removeItem,
    addCheckoutItem,
    removeCheckoutItem,
    getSummary,
    resetState,
  }
})
